<div class="marketplace-page">
  <!-- Hero Section -->
  <section class="hero-section">
    <div class="hero-background-image"></div>
    <div class="hero-overlay"></div>
    <div class="hero-content">
      <h1 class="hero-title">Discover Amazing Stores</h1>
      <p class="hero-subtitle">Shop from thousands of trusted merchants across all categories</p>
      <div class="hero-stats">
        <div class="hero-stat">
          <span class="stat-number">10K+</span>
          <span class="stat-label">Stores</span>
        </div>
        <div class="hero-stat">
          <span class="stat-number">1M+</span>
          <span class="stat-label">Products</span>
        </div>
        <div class="hero-stat">
          <span class="stat-number">500K+</span>
          <span class="stat-label">Customers</span>
        </div>
      </div>
    </div>
  </section>

  <!-- Shop by Category (above search) -->
  @if (!loading() && !error()) {
    <section class="featured-categories-section">
      <h2 class="section-heading">Shop by Category</h2>
      <div class="categories-preview">
        @for (category of categories; track category.value) {
          <div class="category-preview-card" (click)="selectedCategory.set(category.value); onCategoryChange()">
            <div class="category-icon-wrapper">
              <i [class]="'pi ' + category.icon"></i>
            </div>
            <span class="category-label">{{ category.label }}</span>
          </div>
        }
      </div>
    </section>
  }

  <!-- Search for stores (below categories) -->
  <section class="filters-section">
    <div class="filters-container">
      <div class="search-wrapper">
        <div class="search-box">
          <i class="pi pi-search search-icon"></i>
          <input 
            type="text" 
            [(ngModel)]="searchQuery"
            placeholder="Search for stores..."
            class="search-input"
          />
          @if (searchQuery()) {
            <button 
              type="button" 
              class="clear-search"
              (click)="searchQuery.set('')"
            >
              <i class="pi pi-times"></i>
            </button>
          }
        </div>
      </div>
    </div>
  </section>

  <!-- Stores on promo (carousel + View discount stores) -->
  @if (promoStores().length > 0 && !loading()) {
    <section class="promo-stores-section">
      <div class="promo-stores-header">
        <h2 class="section-heading promo-heading">
          <i class="pi pi-tag promo-heading-icon"></i>
          Stores on promo
        </h2>
        <button type="button" class="view-discount-stores-btn" (click)="viewDiscountStores()">
          <span>View discount stores</span>
          <i class="pi pi-arrow-right"></i>
        </button>
      </div>
      <div class="promo-stores-carousel">
        @for (platform of promoStores(); track platform.id) {
          <div class="promo-store-card" (click)="onPlatformClick(platform)">
            <div class="promo-store-card-inner">
              @if (platform.promoLabel || (platform.maxDiscountPercentage != null && platform.maxDiscountPercentage > 0)) {
                <span class="promo-badge">{{ platform.promoLabel ?? (platform.maxDiscountPercentage + '% off') }}</span>
              }
              <div class="promo-store-logo-wrap">
                <img 
                  [src]="getLogoUrl(platform.platformLogo)" 
                  [alt]="platform.platformName"
                  class="promo-store-logo"
                  (error)="$any($event.target).src='https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=300&h=300&fit=crop'"
                />
              </div>
              <h3 class="promo-store-name">{{ platform.platformName }}</h3>
              @if (platform.businessName) {
                <p class="promo-store-business">{{ platform.businessName }}</p>
              }
              <span class="promo-store-cta">Shop deals <i class="pi pi-arrow-right"></i></span>
            </div>
          </div>
        }
      </div>
    </section>
  }

  <!-- Main Content -->
  <main class="marketplace-container">
    <!-- Loading State -->
    @if (loading()) {
      <div class="marketplace-preloader">
        <div class="phone-preloader">
          <div class="phone-frame">
            <div class="phone-screen">
              <div class="phone-notch"></div>
              <div class="rolling-ball-container">
                <div class="rolling-ball"></div>
              </div>
              <div class="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
        <p class="preloader-text">Loading stores...</p>
      </div>
    }

    <!-- Error State -->
    @if (error() && !loading()) {
      <div class="error-container">
        <div class="error-icon">⚠️</div>
        <p class="error-message">{{ error() }}</p>
        <p-button label="Retry" (onClick)="loadPlatforms()" [raised]="true" severity="primary"></p-button>
      </div>
    }

  <!-- Platforms Grid -->
  @if (!loading() && !error() && filteredPlatforms().length > 0) {
    <section class="platforms-section">
      <div class="platforms-section-header">
        <h2 class="section-heading">
          {{ showPromoOnly() ? 'Discount stores' : getSelectedCategoryLabel() }}
        </h2>
        @if (showPromoOnly()) {
          <button type="button" class="clear-promo-filter-btn" (click)="clearPromoFilter()">
            <i class="pi pi-times"></i> Show all stores
          </button>
        }
      </div>
      <div class="platforms-grid">
        @for (platform of filteredPlatforms(); track platform.id) {
          <div class="platform-card">
            <div class="platform-card-header">
              <div class="logo-container" (click)="onPlatformClick(platform)">
                <img 
                  [src]="getLogoUrl(platform.platformLogo)" 
                  [alt]="platform.platformName"
                  class="platform-logo"
                  (error)="$any($event.target).src='https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=300&h=300&fit=crop'"
                />
              </div>
              @if (platform.country) {
                <div class="country-badge">
                  <i class="pi pi-map-marker"></i>
                  <span>{{ platform.country }}</span>
                </div>
              }
            </div>
            <div class="platform-card-content">
              <h3 class="platform-name" (click)="onPlatformClick(platform)">{{ platform.platformName }}</h3>
              @if (platform.businessName) {
                <p class="business-name">{{ platform.businessName }}</p>
              }
              <button 
                type="button"
                class="view-store-btn"
                (click)="onPlatformClick(platform); $event.stopPropagation()"
              >
                <i class="pi pi-store"></i>
                <span>Explore Store</span>
                <i class="pi pi-arrow-right"></i>
              </button>
            </div>
          </div>
        }
      </div>
    </section>
    }

    <!-- Empty State -->
    @if (!loading() && !error() && filteredPlatforms().length === 0 && platforms().length > 0) {
      <div class="empty-container">
        <div class="empty-icon">🔍</div>
        <h2>No stores found</h2>
        <p>Try adjusting your search or filters</p>
        <p-button 
          label="Clear Filters" 
          (onClick)="searchQuery.set(''); selectedCategory.set(null)" 
          [text]="true"
          severity="secondary"
        ></p-button>
      </div>
    }

    <!-- No Stores State -->
    @if (!loading() && !error() && platforms().length === 0) {
      <div class="empty-container">
        <div class="empty-icon">🏪</div>
        <h2>No stores available</h2>
        <p>There are currently no stores in the marketplace.</p>
      </div>
    }
  </main>

  <!-- Footer -->
  <footer class="footer">
    <div class="footer-content">
      <div class="footer-section">
        <h4>ArkifiHub</h4>
        <p>The marketplace for trusted stores</p>
      </div>
      <div class="footer-section">
        <h4>For Store Owners</h4>
        <ul>
          <li><a href="#">Open a Store</a></li>
          <li><a href="#">Seller Guidelines</a></li>
          <li><a href="#">Help Center</a></li>
        </ul>
      </div>
      <div class="footer-section">
        <h4>Company</h4>
        <ul>
          <li><a href="#">About Us</a></li>
          <li><a href="#">Contact</a></li>
          <li><a href="#">Privacy Policy</a></li>
        </ul>
      </div>
    </div>
    <div class="footer-bottom">
      <p>&copy; 2026 ArkifiHub. All rights reserved.</p>
    </div>
  </footer>
</div>